class BathMenuPanels extends HTMLElement {
  connectedCallback() {
    this.hoverTimer = null;
    this.pendingTab = null;
    this.tabs = Array.from(this.querySelectorAll('[data-bath-tab]'));
    this.panels = new Map(
      Array.from(this.querySelectorAll('[data-bath-panel]')).map((panel) => [panel.id, panel])
    );

    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('pointerout', this.onPointerOut);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);

    const initialTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.tabs[0];
    if (initialTab) this.select(initialTab);
  }

  disconnectedCallback() {
    this.cancelPendingHover();
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('pointerout', this.onPointerOut);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
  }

  tabForTarget(target) {
    if (!(target instanceof Element)) return null;
    const row = target.closest('.bath-menu-tabs__side-item');
    if (!row || !this.contains(row)) return null;
    return row.querySelector('[data-bath-tab]');
  }

  onPointerOver = (event) => {
    if (event.pointerType === 'touch') return;
    const tab = this.tabForTarget(event.target);
    if (!tab || tab === this.pendingTab) return;
    this.cancelPendingHover();
    if (tab.getAttribute('aria-selected') === 'true') return;

    this.pendingTab = tab;
    this.hoverTimer = window.setTimeout(() => {
      this.pendingTab = null;
      this.hoverTimer = null;
      this.select(tab);
    }, 180);
  };

  onPointerOut = (event) => {
    const row = event.target instanceof Element ? event.target.closest('.bath-menu-tabs__side-item') : null;
    if (!row || !this.contains(row)) return;
    if (event.relatedTarget instanceof Node && row.contains(event.relatedTarget)) return;
    if (row.querySelector('[data-bath-tab]') === this.pendingTab) this.cancelPendingHover();
  };

  onFocusIn = (event) => {
    const tab = this.tabForTarget(event.target);
    if (tab) {
      this.cancelPendingHover();
      this.select(tab);
    }
  };

  onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-bath-tab]') : null;
    if (!tab || !this.contains(tab)) return;
    this.cancelPendingHover();
    this.select(tab);
  };

  onKeyDown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-bath-tab]') : null;
    if (!tab || !this.contains(tab)) return;

    const currentIndex = this.tabs.indexOf(tab);
    let nextIndex;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.cancelPendingHover();
    const nextTab = this.tabs[nextIndex];
    this.select(nextTab);
    nextTab.focus();
  };

  cancelPendingHover() {
    if (this.hoverTimer !== null) window.clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
    this.pendingTab = null;
  }

  select(tab) {
    if (tab.getAttribute('aria-selected') === 'true' && this.dataset.enhanced === 'true') return;

    for (const item of this.tabs) {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
      const panel = this.panels.get(item.getAttribute('aria-controls'));
      if (panel) panel.hidden = !active;
    }

    this.dataset.enhanced = 'true';
    requestAnimationFrame(() => {
      const submenu = this.closest('.menu-list__submenu');
      const header = this.closest('header-component');
      const topLink = this.closest('.menu-list__list-item')?.querySelector('.menu-list__link');
      if (!submenu || !header || topLink?.getAttribute('aria-expanded') !== 'true') return;

      const height = submenu.offsetHeight;
      header.style.setProperty('--submenu-height', `${height}px`);
      header.style.setProperty('--full-open-header-height', `${header.offsetHeight + height}px`);
    });
  }
}

if (!customElements.get('bath-menu-panels')) {
  customElements.define('bath-menu-panels', BathMenuPanels);
}
